import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { captureException } from "@sentry/react-native";
import { Currency } from "currency";

import {
  Carrier,
  LatLng,
  MapData,
  PickupAddress,
  PickupSchedule,
} from "../types";

type ContextAPI = {
  manualFulfillmentsEnabled: boolean;
  automaticFulfillmentsEnabled: boolean;
  updateAutomaticFulfillmentEnabled: (enabled: boolean) => Promise<void>;

  fulfillmentOptions: FulfillmentOption[];
  fetchFulfillmentOptions: () => void;

  savePickupAddress: (pickupAddress: PickupAddress) => Promise<PickupAddress>;
  getPickupAddress: () => Promise<PickupAddress>;
  getPickupSchedules: (params: LatLng) => Promise<PickupSchedule[]>;

  navigateToLegacyFulfillment: () => void;
};

type ContextType = ContextAPI & {
  pickupAddress: PickupAddress | null;
  pickupSchedules: PickupSchedule[] | null;
  selectedMapData: MapData | null;
  setSelectedMapData: (data: MapData | null) => void;
};
export type { ContextAPI as TDeliveryOptions };

const Context = createContext<ContextType | null>(null);

export default function useDeliveryFulfillments() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("Missing DeliveryFulfillmentsProvider");
  return ctx;
}

export function DeliveryFulfillmentsProvider({
  children,
  ...props
}: PropsWithChildren<ContextAPI>) {
  const [pickupSchedules, setPickupSchedules] = useState<
    PickupSchedule[] | null
  >(null);
  const [pickupAddress, setPickupAddress] = useState<PickupAddress | null>(
    null
  );

  const [selectedMapData, setSelectedMapData] = useState<MapData | null>(null);

  const { fetchFulfillmentOptions, getPickupAddress, getPickupSchedules } =
    props;

  const latitude = pickupAddress?.geolocation.latitude;
  const longitude = pickupAddress?.geolocation.longitude;

  useEffect(() => {
    fetchFulfillmentOptions();
    getPickupAddress().then(setPickupAddress).catch(captureException);
    if (latitude && longitude) {
      getPickupSchedules({ latitude, longitude })
        .then(setPickupSchedules)
        .catch(captureException);
    }
  }, [
    latitude,
    longitude,
    fetchFulfillmentOptions,
    getPickupAddress,
    getPickupSchedules,
  ]);

  async function savePickupAddress(pickupAddress: PickupAddress) {
    const savedPickupAddress = await props.savePickupAddress(pickupAddress);
    setPickupAddress(savedPickupAddress);
    return savedPickupAddress;
  }

  return (
    <Context.Provider
      value={{
        ...props,
        pickupAddress,
        pickupSchedules,
        savePickupAddress,
        selectedMapData,
        setSelectedMapData,
      }}
    >
      {children}
    </Context.Provider>
  );
}

type FulfillmentOption = {
  uuid: string;
  description: string;
  price: number;
  published: boolean;
  carrier_name: Carrier;
  currency: Currency;
};
